import React, {
  useRef,
  useImperativeHandle,
  useCallback,
  useMemo,
  useState,
  useEffect
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import * as Yup from 'yup';
import PropTypes from 'prop-types';
import { isEmpty, isArray } from 'lodash';

import api from '~/services/api';
import { Modal, Tabs, Spinner } from '~/components';
import { t } from '~/helpers';
import { updatePostoPlanejamento } from '~/store/modules/postoplanejamento/actions';

import {
  DadosPostoPlanejamento,
  DadosHorarios,
  DadosEstoque,
  DadosGPP,
} from '../Tabs';

// import { ADM } from '~/constants/perfil';

const Form = React.forwardRef(({ formEdit, onCloseModal }, ref) => {
  const dispatch = useDispatch();

  const { profile } = useSelector((state) => state.user);
  const redux = useSelector((state) => state.postoplanejamento.params);

  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);

  const modalRef = useRef(null);
  const tabRef = useRef(null);
  const formRef = useRef(ref);

  const tabs = useMemo(
    () => [
      {
        label: 'Dados',
        component: (rest) => <DadosPostoPlanejamento data={data} {...rest} />,
        validate: {
          orga_codigo: Yup.number()
            .typeError(t('s.geral.validacao.campoobrigatorio'))
            .required(t('s.geral.validacao.campoobrigatorio')),
          unid_codigo: Yup.number()
            .typeError(t('s.geral.validacao.campoobrigatorio'))
            .required(t('s.geral.validacao.campoobrigatorio')),
          poco_endereco: Yup.string()
            .typeError(t('s.geral.validacao.campoobrigatorio'))
            .required(t('s.geral.validacao.campoobrigatorio')),
          poco_cep: Yup.string()
            .typeError(t('s.geral.validacao.campoobrigatorio'))
            .required(t('s.geral.validacao.campoobrigatorio')),
          muni_codigo: Yup.number()
            .typeError(t('s.geral.validacao.campoobrigatorio'))
            .required(t('s.geral.validacao.campoobrigatorio')),
        },
      },
      {
        label: 'Horários',
        component: () => <DadosHorarios data={data} />,
        validate: {},
      },
      {
        label: 'Estoque',
        component: () => <DadosEstoque data={data} />,
        validate: {},
      },
      {
        label: 'GP',
        component: () => <DadosGPP dataPosto={data} />,
        validate: {},
      },
    ],
    [data]
  );

  const closeModal = useCallback(() => {
    modalRef.current.handleCloseModal();
    onCloseModal();
  }, [onCloseModal]);

  const actions = useMemo(
    () => [
      {
        label: 'Confirmar',
        callback: () => tabRef.current.onValidate(),
        color: 'primary',
        variant: 'outlined',
      },
      {
        label: 'Cancelar',
        callback: () => closeModal(),
        color: 'secondary',
        variant: 'outlined',
      },
    ],
    []
  );

  const handleResponseAPI = useCallback(async () => {
    try {
      setLoading(true);

      const { poco_codigo } = formEdit;
      const response = await api.get(
        `postoplanejamento/completo/${poco_codigo}`
      );

      // estrutura o campo de dados

      let dados = response.data;

      let dadosPostoPlanejamento = {
        poco_codigo: dados.posto.poco_codigo,
        poco_descricao: dados.posto.poco_descricao,
        poco_desc_abrev: dados.posto.poco_desc_abrev,
        poco_nome_fantasia: dados.posto.poco_nome_fantasia,
        orga_codigo: dados.posto.orga_codigo,
        unid_codigo: dados.posto.unid_codigo,
        poco_endereco: dados.posto.poco_endereco,
        poco_cep: dados.posto.poco_cep,
        muni_codigo: dados.posto.muni_codigo,
        poco_telefone: dados.posto.poco_telefone,
        poco_status: dados.posto.poco_status,
      };

      let dataEdicao = { ...dados, ...dadosPostoPlanejamento };

      setData(dataEdicao);
    } catch (err) {
      if (isArray(err)) err.map((e) => toast.error(t(e)));
      else toast.error(t(err));
    } finally {
      setLoading(false);
    }
  }, [formEdit]);

  const handleSubmit = React.useCallback(
    async (values) => {
      const dados = { ...data, ...values };

      if (dados.erroDiasAtendimento === true) {
        toast.error('Por favor preencha corretamente os dias de atendimento');
        return;
      }

      if (dados.erroDiasAlmoco === true) {
        toast.error('Por favor preencha corretamente os dias de almoço');
        return;
      }

      if (dados.erroFeriados === true) {
        toast.error(
          'Por favor preencha corretamente os feriados. Não pode ter repetição de data'
        );
        return;
      }

      if (dados.erroEstoque === true) {
        toast.error(
          'Ponto de ressuprimento deve ser menor do que a capacidade'
        );
        return;
      }

      // configura o posto

      let posto = dados.posto;

      posto.poco_codigo = values.fields.poco_codigo;
      posto.poco_descricao = values.fields.poco_descricao;
      posto.poco_desc_abrev = values.fields.poco_desc_abrev;
      posto.poco_nome_fantasia = values.fields.poco_nome_fantasia;
      posto.orga_codigo = values.fields.orga_codigo;
      posto.unid_codigo = values.fields.unid_codigo;
      posto.poco_endereco = values.fields.poco_endereco;
      posto.poco_cep = values.fields.poco_cep;
      posto.muni_codigo = values.fields.muni_codigo;
      posto.poco_telefone = values.fields.poco_telefone;
      posto.poco_status = values.fields.poco_status;

      const objSend = {
        posto: posto,
        tanquesFisicos: dados.tanquesFisicos,
        datasFeriado: dados.datasFeriado,
        diasAtendimento: dados.diasAtendimento,
        diasAlmoco: dados.diasAlmoco,
      };

      try {
        await dispatch(updatePostoPlanejamento(objSend, redux));
        closeModal();
      } catch (err) {
        toast.error(err);
      }
    },
    [data, redux]
  );

  const openModal = useCallback(() => {
    modalRef.current.handleOpenModal();
    if (!isEmpty(formEdit)) handleResponseAPI();
  }, [formEdit]);

  useImperativeHandle(ref, () => ({
    openModal,
  }));



  return (
    <Modal
      ref={modalRef}
      title="Posto Planejamento e Controle"
      component={() => (
        <>
          <Spinner loading={loading} />
          <Tabs
            ref={tabRef}
            color="default"
            indicatorColor="primary"
            textColor="primary"
            tabs={tabs}
            getData={(value) => handleSubmit(value)}
            initials={data}
          />
        </>
      )}
      actions={actions}
    />
  );
});

Form.propTypes = {
  formEdit: PropTypes.objectOf(PropTypes.any),
  onCloseModal: PropTypes.func.isRequired,
};

Form.defaultProps = {
  formEdit: {},
};

export default Form;
